import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server'
import { Divider, message, Modal, Trans } from '@/components'
import { useCancelSnapshot } from '@/hooks/useCancelSnapshot'
import { Instance } from '@/models/instance'
import { useTeamMemberInstanceListQuery } from '@/pages/teams/member/hooks/useTeamMemberInstanceListQuery'

export interface FilterItem {
  label: ReactNode
  value: string
  codes: number[]
}

export function useTeamMemberInstances(opts: { teamResID: string; memberResID: string }) {
  const { teamResID, memberResID } = opts
  const { t } = useTranslation()

  const statusFilters: FilterItem[] = [
    {
      label: <Trans i18nKey="instance.list.statusLabelRunning">Running</Trans>,
      value: 'active',
      codes: [1, 2, 7, 15, 16],
    },
    {
      label: <Trans i18nKey="instance.list.statusLabelSnapshotting">Snapshotting</Trans>,
      value: 'snapshotting',
      codes: [8, 9, 10, 11, 12],
    },
    {
      label: <Trans i18nKey="instance.list.statusLabelReleased">Released</Trans>,
      value: 'released',
      codes: [3, 4, 6],
    },
  ]

  const [filter, setFilter] = useState<FilterItem | undefined>(statusFilters[0])

  const [page, setPage] = useState(1)
  const pageSize = 10

  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [regions, setRegions] = useState<number[]>([])
  const [regionFilterCriteria, setRegionFilterCriteria] = useState<number>(-1)
  const [createSnapshotInstance, setCreateSnapshotInstance] = useState<Instance | undefined>()

  useEffect(() => {
    ;(async () => {
      try {
        const res = await API.getDomainList()
        if (res.code === 0) {
          setDomainInfos(res.domainInfos || [])
          setRegions((res.domainInfos || []).map((d) => d.domain))
        }
      } catch (e) {
        console.error(e)
      }
    })()
  }, [])

  const { instances, total, isLoading, refetch } = useTeamMemberInstanceListQuery({
    teamResID,
    memberResID,
    page,
    pageSize,
    statusCodes: filter?.codes,
    regions,
    regionFilterCriteria,
  })

  useEffect(() => {
    setPage(1)
  }, [filter?.value, regionFilterCriteria, regions.join(',')])

  const onRelease = useCallback(
    async (data: Instance) => {
      await Modal.confirm({
        title: t('instance.releaseModal.title'),
        content: (
          <div className="bg-panel-gray-100 rounded-lg p-4 flex-row mb-4">
            <div>
              <div className="text-[20px] leading-7 font-bold  text-primary">
                {data.displayTitle}
              </div>
            </div>
            <Divider className="my-3" />
            <div>
              <div className="t-sb text-secondary font-medium">
                <Trans i18nKey="instance.releaseModal.content">
                  The machine will be stopped and released, please save the environment if you need
                  it.
                </Trans>
              </div>
              <div className=" text-btn-secondary t-sb cursor-pointer">
                <Trans i18nKey="instance.releaseModal.whySaveEnvironment">
                  Why save the environment?
                </Trans>
              </div>
            </div>
          </div>
        ),
        confirmBtnCfg: {
          text: t('instance.releaseModal.stopReleaseBtn'),
        },
        className: 'w-[800px]',
      })
      const res = await API.deleteTeamMemberInstance({ res_id: teamResID, inst_res_id: data.resID })
      if (res.code === 0) {
        message.success(t('common.message.success'))
        refetch()
      }
    },
    [refetch, t],
  )

  const { cancelInstanceSnapshot: onCancelSnapshot } = useCancelSnapshot({ onSuccess: refetch })

  const onUpdate = useCallback(() => refetch(), [refetch])

  const getNodeList = useCallback(
    async (p: number, _force: boolean) => {
      if (p !== page) setPage(p)
      else await refetch()
    },
    [page, refetch],
  )

  return {
    teamResID,
    memberResID,
    // States
    filter,
    setFilter,
    page,
    setPage,
    total,
    instaceList: instances,
    loading: isLoading,
    domainInfos,
    regions,
    setRegions,
    regionFilterCriteria,
    setRegionFilterCriteria,
    createSnapshotInstance,
    setCreateSnapshotInstance,

    // Methods
    getNodeList,
    onRelease,
    onCancelSnapshot,
    onUpdate,

    // Constants
    statusFilters,
    pageSize,
    REGION_GLOBAL: -1,
    REGION_DATA_DRIVE: -2,
    REGION_MESH_CLUSTERING: -3,
  }
}
